import React from 'react'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

import ChartTotalEmpresasView from '../../the-consumer-market/views/tcm_chart_total_empresas'
import AtividadeEconomicaTableView from '../../the-consumer-market/views/tcm_ativ_economica'
import ChartAbertasFechadasView from '../../the-consumer-market/views/tcm_chart_abertas_fechadas'
import { CnpjPageScope } from '../mv_scopes'
import { LoadError, WordCloudView } from './mv_components'
import { PointsMapView } from './mv_points_map'
import { EmpresasTableView } from './mv_tables'

const useStyles = makeStyles()({
    page: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0
    },
    topGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.12fr) minmax(0, 1fr) minmax(0, 1.2fr)',
        gridTemplateRows: '88px 470px',
        gridTemplateAreas: `
            "kpis nuvem mapa"
            "secundarias nuvem mapa"
        `,
        gap: 10,
        '@media (max-width: 1240px)': {
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gridTemplateRows: '88px 470px 420px',
            gridTemplateAreas: `
                "kpis nuvem"
                "secundarias nuvem"
                "mapa mapa"
            `
        },
        '@media (max-width: 720px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: '88px 360px 360px 380px',
            gridTemplateAreas: '"kpis" "secundarias" "nuvem" "mapa"'
        }
    },
    bottomGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 2.12fr) minmax(0, 1.2fr)',
        gridTemplateRows: '470px',
        gridTemplateAreas: '"detalhe mercado"',
        gap: 10,
        '@media (max-width: 1240px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: '520px 470px',
            gridTemplateAreas: '"detalhe" "mercado"'
        }
    },
    kpis: {
        gridArea: 'kpis',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 8
    },
    nuvem: { gridArea: 'nuvem' },
    secundarias: { gridArea: 'secundarias' },
    mapa: { gridArea: 'mapa' },
    detalhe: { gridArea: 'detalhe' },
    mercado: { gridArea: 'mercado' },
    mercadoBody: {
        display: 'grid',
        gridTemplateRows: '78px minmax(0, 1fr)',
        gap: 10,
        flexGrow: 1,
        minHeight: 0
    },
    mercadoKpis: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 8
    },
    chart: {
        minHeight: 0,
        '&&': { boxShadow: 'none', border: 0, borderRadius: 0, backgroundColor: 'transparent' },
        '&& > div:first-of-type': { paddingLeft: 0, paddingRight: 0 }
    },
    detalharPage: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0,
        height: '100%'
    },
    detalharTable: {
        height: 'calc(100vh - 132px)',
        minHeight: 480
    }
})

const SubCaption = styled('div')({
    marginBottom: 8,
    fontSize: 12,
    color: 'var(--muted)'
})

type CnpjPageViewProps = {
    className?: string
    scope: CnpjPageScope
}

class CnpjPageViewClass implements FCClassContext<CnpjPageViewProps> {
    scope!: CnpjPageScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState({ scope }: CnpjPageViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    render(props: CnpjPageViewProps) {
        return this.scope.detalhar ? this.renderDetalhar(props) : this.renderPainel(props)
    }

    renderPainel({ className }: CnpjPageViewProps) {
        const { classes, scope } = this
        return (
            <div className={className}>
                <div className={classes.page}>
                    <LoadError message={scope.erro} />

                    <div className={classes.topGrid}>
                        <div className={classes.kpis}>
                            <ChartTotalEmpresasView scope={scope.totalEmpresas} />
                            <ChartTotalEmpresasView scope={scope.totalAtivas} />
                            <ChartTotalEmpresasView scope={scope.totalFuncionarios} />
                        </div>
                        <WordCloudView className={classes.nuvem} scope={scope.nuvem} />
                        <AtividadeEconomicaTableView
                            className={classes.secundarias}
                            scope={scope.secundarias}
                            compact
                        />
                        <PointsMapView
                            className={classes.mapa}
                            scope={scope.mapa}
                            ferramentas={scope.mapaFerramentas}
                        />
                    </div>

                    <div className={classes.bottomGrid}>
                        <EmpresasTableView className={classes.detalhe} scope={scope.detalhamento} />
                        <TcmCard className={classes.mercado} caption="Mercado de Atividades Veterinárias">
                            <SubCaption>Atividade Econômica Principal</SubCaption>
                            <div className={classes.mercadoBody}>
                                <div className={classes.mercadoKpis}>
                                    <ChartTotalEmpresasView scope={scope.mercadoEmpresas} />
                                    <ChartTotalEmpresasView scope={scope.mercadoAtivas} />
                                    <ChartTotalEmpresasView scope={scope.mercadoNovas} />
                                </div>
                                <ChartAbertasFechadasView className={classes.chart} scope={scope.novasEmpresas} />
                            </div>
                        </TcmCard>
                    </div>
                </div>
            </div>
        )
    }

    renderDetalhar({ className }: CnpjPageViewProps) {
        const { classes, scope } = this
        return (
            <div className={className}>
                <div className={classes.detalharPage}>
                    <LoadError message={scope.erro} />
                    {scope.detalhar && <EmpresasTableView className={classes.detalharTable} scope={scope.detalhar} />}
                </div>
            </div>
        )
    }
}

export const CnpjPageView = classToFComponent(CnpjPageViewClass, React)
